import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { SubscriptionsApi } from '../../api/subscriptions.api';
import { SubscriptionsApiActions } from './subscriptions.actions';
import * as errorHelpers from 'src/app/helpers/error.helpers';
import { selectSubscritionsProducts } from 'app/store/products/products.selectors';
import { ToasterService } from 'app/services/toaster.service';

@Injectable()
export class SubscriptionsEffects {
  constructor(
    private api: SubscriptionsApi,
    private actions$: Actions,
    private store: Store,
    private toasterService: ToasterService,
    private translateService: TranslateService
  ) { }

  // fetchPlansWhenInitSubscriptionsPageComponent$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(SubscriptionsApiActions.initSubscriptionsPageComponent),
  //     map(() => {
  //       return PlansApiActions.fetchPlans();
  //     })
  //   );
  // });

  fetchActiveSubscriptionWhenInitSubscriptionsPageComponent$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SubscriptionsApiActions.initSubscriptionsPageComponent),
        map(() => {
          return SubscriptionsApiActions.fetchActiveSubscription();
        })
      );
    }
  );

  loadActiveSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionsApiActions.fetchActiveSubscription),
      switchMap(() => this.store.select(selectSubscritionsProducts)),
      filter((plans) => {
        return plans.length > 0;
      }),
      switchMap((plans) => {
        return this.api.getActiveSubscription$(plans).pipe(
          map((activeSubscription) => {
            return SubscriptionsApiActions.retrievedActiveSubscription({
              activeSubscription,
            });
          }),
          catchError((err: HttpErrorResponse) => {
            if (err.status === 404) {
              return of(
                SubscriptionsApiActions.retrievedActiveSubscription({
                  activeSubscription: null,
                })
              );
            }
            return of(
              SubscriptionsApiActions.failed({
                errorMessage: errorHelpers.getErrorMessage(
                  err,
                  this.translateService
                ),
              })
            );
          })
        );
      })
    );
  });

  loadFormerSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionsApiActions.fetchFormerSubscriptions),
      switchMap(() => this.store.select(selectSubscritionsProducts)),
      filter((plans) => {
        return plans.length > 0;
      }),
      switchMap((plans) => {
        return this.api.getSubscriptionsHistory$(plans).pipe(
          map((formerSubscriptions) => {
            return SubscriptionsApiActions.retrievedFormerSubscriptions({
              formerSubscriptions,
            });
          }),
          catchError((err) => {
            return of(
              SubscriptionsApiActions.failed({
                errorMessage: errorHelpers.getErrorMessage(
                  err,
                  this.translateService
                ),
              })
            );
          })
        );
      })
    );
  });

  loadFamilyMembers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionsApiActions.fetchFamilyMembers),
      switchMap(() => {
        return this.api.getFamilyMembers$().pipe(
          map((memberEmails) => {
            return SubscriptionsApiActions.retrievedFamilyMembers({
              memberEmails,
            });
          }),
          catchError((err) => {
            return of(
              SubscriptionsApiActions.failed({
                errorMessage: errorHelpers.getErrorMessage(
                  err,
                  this.translateService
                ),
              })
            );
          })
        );
      })
    );
  });

  addFamilyMember$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionsApiActions.postFamilyMember),
      switchMap((action) => {
        return this.api.addFamilyMember$(action.email).pipe(
          map(() => {
            return SubscriptionsApiActions.fetchFamilyMembers();
          }),
          catchError((err) => {
            let errorMessage!: string;
            if (!!err?.error?.error) {
              errorMessage =
                err.error.error.charAt(0).toUpperCase() +
                err.error.error.slice(1);
            } else {
              errorMessage = errorHelpers.getErrorMessage(
                err,
                this.translateService
              );
            }
            return of(SubscriptionsApiActions.failed({ errorMessage }));
          })
        );
      })
    );
  });

  deleteFamilyMember$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionsApiActions.deleteFamilyMember),
      switchMap((action) => {
        return this.api.deleteFamilyMember$(action.email).pipe(
          map(() => {
            return SubscriptionsApiActions.fetchFamilyMembers();
          }),
          catchError((err) => {
            return of(
              SubscriptionsApiActions.failed({
                errorMessage: errorHelpers.getErrorMessage(
                  err,
                  this.translateService
                ),
              })
            );
          })
        );
      })
    );
  });

  displayErrorMessage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SubscriptionsApiActions.failed),
        tap((action) => {
          this.toasterService.error(null, action.errorMessage);
        })
      );
    },
    { dispatch: false }
  );
}
